import { useState, useRef, memo } from 'react'
import { toast } from 'sonner'
import { Send, Zap, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { TurnPermissions, Player } from '../../../types/turn'
import { QuickActionsBottomSheet } from './QuickActionsBottomSheet'
import { NPCSelector } from './NPCSelector'
import { useMediaUpload } from '@/hooks/useMediaUpload'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { MediaFile } from '@/types/message'

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  // Foco do textarea (mobile esconde a BottomNavBar enquanto digita)
  onFocusChange?: (focused: boolean) => void
  disabled?: boolean
  isPrivateChat?: boolean // Chat privado com outro participante
  tableId?: string // ID da mesa para upload de mídia
  onMediaUpload?: (mediaFile: MediaFile) => void // Callback para upload rápido de mídia

  // Props do sistema de turnos
  permissions?: TurnPermissions
  currentPlayer?: Player | null
  isActive?: boolean // Se os turnos estão ativos

  // Props para configuração de turnos
  isMaster?: boolean

  // Props para seleção de NPCs
  characters?: Character[]
  members?: MemberType[]
  selectedCharacter?: Character | null
  onSelectCharacter?: (character: Character | null) => void

  // Props para responder como NPC (mestre)
  respondingAsNpc?: {
    isActive: boolean
    npcUserId: string | null
    npcName: string | null
  }
  controlledNpcs?: Character[] // Lista de NPCs que o mestre controla
  onRollDice?: () => void
  onToggleRespondAsNpc?: (npc: Character | null) => void
  onLiveSync?: () => void
  onPoll?: () => void
  onLab?: () => void
  /** Ids dos módulos ativados na mesa (gating das ações rápidas) */
  enabledModules?: string[]
  /** Sessão da missão ativa: iniciar/encerrar, que vieram da MissionSessionBar */
  sessaoDaMissao?: {
    aberta: boolean
    onIniciar: () => Promise<void>
    onEncerrar: () => Promise<void>
  } | null
}

// Custom comparison function for React.memo - prevents unnecessary re-renders
function arePropsEqual(prev: ChatInputProps, next: ChatInputProps): boolean {
  // Hot path - input value and handlers
  if (prev.value !== next.value) return false
  if (prev.onChange !== next.onChange) return false
  if (prev.onSend !== next.onSend) return false
  if (prev.onFocusChange !== next.onFocusChange) return false
  if (prev.disabled !== next.disabled) return false

  // Boolean flags
  if (prev.isPrivateChat !== next.isPrivateChat) return false
  if (prev.isActive !== next.isActive) return false
  if (prev.isMaster !== next.isMaster) return false

  // IDs
  if (prev.tableId !== next.tableId) return false

  // Permissions (shallow compare key fields)
  if (prev.permissions?.canSendMessage !== next.permissions?.canSendMessage) return false
  if (prev.permissions?.isMyTurn !== next.permissions?.isMyTurn) return false
  if (prev.permissions?.isMaster !== next.permissions?.isMaster) return false

  // Current player
  if (prev.currentPlayer?.id !== next.currentPlayer?.id) return false
  if (prev.currentPlayer?.name !== next.currentPlayer?.name) return false
  // Presença: decide se a linha "Pedir teste" responde ou explica por que não.
  // Sem esta comparação, o aluno da vez cair da rede não repintaria o painel.
  if (prev.currentPlayer?.online !== next.currentPlayer?.online) return false

  // Selected character
  if (prev.selectedCharacter?.id !== next.selectedCharacter?.id) return false

  // NPC state
  if (prev.respondingAsNpc?.isActive !== next.respondingAsNpc?.isActive) return false
  if (prev.respondingAsNpc?.npcUserId !== next.respondingAsNpc?.npcUserId) return false

  // Arrays by reference (should be stable from parent with useMemo)
  if (prev.characters !== next.characters) return false
  if (prev.members !== next.members) return false
  if (prev.controlledNpcs !== next.controlledNpcs) return false

  // Callbacks by reference (should be stable with useCallback)
  if (prev.onMediaUpload !== next.onMediaUpload) return false
  if (prev.onSelectCharacter !== next.onSelectCharacter) return false
  if (prev.onRollDice !== next.onRollDice) return false
  if (prev.onToggleRespondAsNpc !== next.onToggleRespondAsNpc) return false
  if (prev.onLiveSync !== next.onLiveSync) return false
  if (prev.onPoll !== next.onPoll) return false
  if (prev.onLab !== next.onLab) return false

  // Array by reference (estável no GameSession — muda uma vez após o fetch)
  if (prev.enabledModules !== next.enabledModules) return false

  // Sessão da missão (objeto memoizado no GameSession)
  if (prev.sessaoDaMissao !== next.sessaoDaMissao) return false

  return true
}

export const ChatInput = memo(function ChatInput({
  value,
  onChange,
  onSend,
  onFocusChange,
  disabled = false,
  isPrivateChat = false,
  tableId,
  onMediaUpload,
  permissions,
  currentPlayer,
  isActive = false,
  isMaster = false,
  characters = [],
  members = [],
  selectedCharacter = null,
  onSelectCharacter,
  onRollDice,
  respondingAsNpc,
  controlledNpcs = [],
  onToggleRespondAsNpc,
  onLiveSync,
  onPoll,
  onLab,
  enabledModules = [],
  sessaoDaMissao = null
}: ChatInputProps) {
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Ref do seletor de arquivos (disparado pelo painel de acoes rapidas)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { uploadMedia, progress } = useMediaUpload()

  // Determinar se o chat deve estar desabilitado baseado no sistema de turnos
  const isTurnDisabled = isActive && permissions && !permissions.canSendMessage
  const isInputDisabled = disabled || isTurnDisabled || isUploading

  /**
   * "Pedir teste": convocar quem tem a vez a testar um atributo (fase 1 de
   * docs/PLANO_ROLAGEM_CLASSICA.md).
   *
   * O motivo do bloqueio é ESPELHO dos 400 do servidor, não a regra: quem
   * decide continua sendo o backend, que resolve a vez de novo no instante do
   * pedido. Aqui só se antecipa o que dá para saber pela tela — e, quando não
   * dá (o convocado não tem ficha), a recusa chega como toast do servidor.
   *
   * **Rodada aberta sem `currentPlayer` não é bloqueio.** `currentPlayer` sai
   * de `players`, que exclui NPC: na vez de um NPC ele é `null` — e a vez do
   * NPC é convocável (a ficha testada é a dele, rola quem o conduz). Barrar
   * aqui fecharia no navegador uma porta que o servidor deixa aberta.
   */
  const pedidoDeTeste = tableId && isMaster
    ? {
        tableId,
        nomeDaVez: currentPlayer?.name ?? null,
        motivoDoBloqueio: !isActive
          ? 'Sem rodada aberta'
          : currentPlayer?.online === false
            ? `${currentPlayer.name} não está na mesa`
            : undefined,
      }
    : null

  // Handler para upload rápido de mídia
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !tableId || !onMediaUpload) return

    // Validar tamanho
    const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error(`Arquivo muito grande. Máximo: ${file.type.startsWith('video/') ? '100MB' : '10MB'}`)
      return
    }

    setIsUploading(true)
    try {
      const tempEntityId = `quick-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      const mediaFile = await uploadMedia(file, {
        category: 'message',
        entityId: tempEntityId,
        tableId,
        isShared: true  // Envio rápido: já compartilha no grupo
      })

      onMediaUpload(mediaFile)
    } catch (error) {
      console.error('Erro no upload rápido:', error)
      toast.error('Erro ao enviar mídia. Tente novamente.')
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  // Placeholder dinâmico baseado no estado do turno
  const getPlaceholder = (): string => {
    if (isPrivateChat) {
      return "Conversa privada..."
    }

    if (isTurnDisabled && currentPlayer) {
      if (permissions?.isMaster) {
        return `Você é o mestre - pode falar a qualquer momento`
      }
      // O aviso vai onde o dedo ja esta. Antes dizia so "aguarde sua vez", sem
      // dizer que o reservado continua aberto — o aluno achava que estava mudo
      // na mesa inteira.
      if (currentPlayer.isNpc) {
        return `É a vez de ${currentPlayer.name} (NPC) — quem fala é o mestre. Você ainda pode usar o reservado.`
      }
      return `A vez é de ${currentPlayer.name}. Você ainda pode falar no reservado.`
    }

    if (isActive && permissions?.isMyTurn) {
      return "É sua vez! Digite sua ação..."
    }

    return "Digite uma mensagem..."
  }

  const handleSend = () => {
    if (!value.trim() || isInputDisabled) return
    onSend()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }


  // O `canUseQuickActions` morreu em 2026-07-27 (decisao 13). Era um cadeado
  // UNICO no raio — `isMaster || (rodada aberta && minha vez)` — e trancava o
  // painel inteiro: fora da vez, o aluno perdia ate a calculadora, que nao tem
  // nada a ver com turno. Agora cada linha responde pela propria regra, e elas
  // sao DIFERENTES: `isTurnDisabled` (rodada aberta && nao e minha vez) nao e a
  // negacao daquele — sem NENHUMA rodada aberta os dois divergem.

  return (
    // Fase B mobile: sem position fixed — o input vive no fluxo da coluna
    // flex (mobile e desktop), o layout pai reserva o espaco por construcao.
    // Antes era fixed bottom-16 no mobile + paddings chutados no conteudo.
    <div className="bg-game-secondary dark:bg-zinc-900/95 backdrop-blur-xl border-t border-game-border-primary dark:border-zinc-800/80 p-2 md:p-3 shadow-game-md dark:shadow-xl">
      {/* Inputs de arquivo ocultos para upload rápido */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      {/* Os inputs de `capture` (camera e video) sairam em 2026-07-27 junto
          com o menu do clipe: no celular o seletor do proprio sistema ja
          oferece camera e galeria, e no computador eles nunca fizeram nada. */}

      {/* Indicador de upload em progresso */}
      {isUploading && (
        <div className="mb-2 px-3 py-2 bg-violet-500/10 border border-violet-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-violet-600 dark:text-violet-300 text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Enviando mídia... {Math.round(progress)}%</span>
            <div className="flex-1 h-1 bg-violet-100 dark:bg-violet-900/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Os dois banners de turno sairam daqui em 2026-07-27 (decisao 2 do
          docs/PLANO_AREA_DO_CHAT.md): o ambar "aguardando turno de X" e o
          esmeralda "E SUA VEZ". Custavam ate 44px permanentes acima do campo
          — no celular, uma mensagem e meia — para dizer o que o placeholder
          dinamico (getPlaceholder) ja diz DENTRO do campo, onde o dedo ja
          esta, e o campo desabilitado ja mostra. Os quatro testes de
          ChatInput.avisoDoTurno.test.tsx sempre verificaram o placeholder,
          nunca o banner: a prova de que o recado nao dependia deles. */}

      {/* Seletor de NPCs - para mestre em chat de grupo OU chat privado */}
      {isMaster && onSelectCharacter && (
        <NPCSelector
          characters={isPrivateChat ? controlledNpcs : characters}
          members={members}
          selectedCharacter={isPrivateChat
            ? (respondingAsNpc?.npcUserId
              ? controlledNpcs.find(npc => npc.userId === respondingAsNpc.npcUserId) || null
              : null)
            : selectedCharacter
          }
          onSelectCharacter={(character) => {
            if (isPrivateChat && onToggleRespondAsNpc) {
              onToggleRespondAsNpc(character)
            } else {
              onSelectCharacter(character)
            }
          }}
          isMaster={isMaster}
        />
      )}


      <div className="flex items-end gap-2">
        {/* Botão de Ações Rápidas (não mostrar para chats privados) */}
        {!isPrivateChat && (
          <QuickActionsBottomSheet
            open={showQuickActions}
            onOpenChange={setShowQuickActions}
            onRollDice={onRollDice}
            onLiveSync={onLiveSync}
            onPoll={onPoll}
            onLab={onLab}
            isMaster={isMaster}
            enabledModules={enabledModules}
            sessaoDaMissao={sessaoDaMissao}
            pedidoDeTeste={pedidoDeTeste}
            // A conta cai na caixa de mensagem em vez de ser enviada direto:
            // quem escreve confere, comenta e envia — e as regras de turno
            // continuam valendo no envio, sem caminho paralelo.
            onLevarContaParaChat={(texto) => onChange(value ? `${value} ${texto}` : texto)}
            onEnviarMidia={tableId && onMediaUpload ? () => fileInputRef.current?.click() : undefined}
            podeEnviarMidia={!isTurnDisabled}
            // `disabled` aqui e so o que trava o painel INTEIRO: upload em
            // curso, chat desconectado. O bloqueio por turno desceu para cada
            // linha (decisao 13) — ver a tabela no JSDoc do painel.
            disabled={isInputDisabled}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "w-10 h-10 p-0 mb-1 rounded-xl transition-all duration-200",
                  "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 hover:scale-105"
                )}
                disabled={isInputDisabled}
                title="Ações rápidas"
              >
                <Zap className="h-5 w-5 transition-all duration-200 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]" />
              </Button>
            }
          />
        )}

        {/* O clipe saiu daqui em 2026-07-27 (fase 6 do
            docs/PLANO_AREA_DO_CHAT.md) e virou uma linha do painel de acoes
            rapidas. Devolveu ~44px de largura ao campo — no celular o campo
            util tinha ~142px, e o problema dele nunca foi altura. Os `input
            type=file` acima ficam: so mudaram de gatilho. */}

        {/* Input de Mensagem */}
        <div className="flex-1 relative">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyPress}
            onFocus={() => onFocusChange?.(true)}
            onBlur={() => onFocusChange?.(false)}
            placeholder={getPlaceholder()}
            disabled={isInputDisabled}
            rows={1}
            className={cn(
              "resize-none rounded-2xl border-game-border-primary dark:border-zinc-700/50 text-game-primary dark:text-zinc-100 placeholder:text-game-muted dark:placeholder:text-zinc-500 focus:border-violet-500/50 focus:ring-violet-500/20 min-h-[44px] max-h-[120px] pr-12 transition-colors",
              isInputDisabled
                ? "bg-game-tertiary dark:bg-zinc-800/30 cursor-not-allowed opacity-60"
                : "bg-game-secondary dark:bg-zinc-800/80",
              isTurnDisabled
                ? "placeholder:text-amber-600/60 dark:placeholder:text-amber-400/60"
                : ""
            )}
          />

          {/* Botão de Enviar */}
          <Button
            onClick={handleSend}
            disabled={isInputDisabled || !value.trim()}
            size="sm"
            className={cn(
              "absolute right-2 bottom-2 w-9 h-9 p-0 rounded-xl transition-all duration-200",
              isInputDisabled || !value.trim()
                ? "bg-game-tertiary dark:bg-zinc-700/50 cursor-not-allowed text-game-muted"
                : "bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white shadow-lg shadow-violet-500/25"
            )}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

    </div>
  )
}, arePropsEqual)